// CameraController — the map viewport-camera Facade (map-architecture Phase 6).
//
// A plain TypeScript class (NOT React). Absorbs the camera logic that used to
// live at MapCanvas component scope:
//   * the singleton viewport-animate wrapper (`runViewportAnimate`)          → `animate()`
//   * the initial "fly to a random owned house" effect body                  → `runInitialHouseCamera()`
//   * the deep-link camera-focus effect's two branches
//     (Telegram startapp: `plot_<id>` | `cityhall`)                          → `zoomToPlot()` / `centerOnCityHall()`
//
// The bodies are moved VERBATIM from MapCanvas; the only change is closure
// variable → context access. The OUTER gating (which reactive flags must be
// true before the camera should move at all — `pendingDeepLinkFocus`,
// `markersReady`, `isPixiAppReady`, `shouldZoomToPlot`, etc.) stays in the
// MapCanvas `useEffect`s exactly where it was — only the effect BODIES
// (what happens once gating passes) move here, mirroring how
// `createHighlightForMapObject`'s 9 call-sites keep their own guards and just
// delegate the highlight itself to HighlightManager.
//
// Reactive values that change every render (`isPixiAppReady`, `userData`,
// `shouldZoomToPlot`, `hasOnboardingCompleted`) are NOT baked into the
// constructor context — this instance is created once via a `useRef` guard and
// would otherwise see a stale first-render snapshot. They're passed as
// call-time parameters, exactly as the original effect closures already had
// them in scope, preserving the original capture timing byte-for-byte.
//
// The onboarding "zoom to free plot" effect (gated by `shouldZoomToPlot`,
// MapCanvas.tsx) is deliberately LEFT IN MapCanvas — its body interleaves
// camera animation with ~6 distinct modal/onboarding state setters
// (setIsOnboardingZooming, setSelectedMapObject, setPlotFreeOnboarding,
// setOpenOnboardingPlotFreeAfterReward, openModal, setShouldZoomToPlot) across
// nested setTimeout chains. Too spliced into that onboarding orchestration to
// extract safely without touching its behavior — safety over line count.
import type { Viewport } from 'pixi-viewport';
import * as PIXI from 'pixi.js';
import { MapObject } from '../../../models/mapObject';
import type { UserData } from '../../../models/user';
import type { DeepLinkFocusTarget } from '../../../stores/useInterfaceStore';
import type { MapModalName, MapModalPayload } from '../../../stores/useMapModalStore';

// Module-level (session) flag — must survive MapCanvas unmount when entering a
// room. Old production (7235cd81) used a top-of-file `let`; after the refactor
// the flag lived on createMapSceneState() analytics and reset on every remount,
// so leaving someone else's house re-fired "fly to a random owned house" and
// the camera jumped to the user's own house instead of staying on the one just left.
let didInitialHouseCamera = false;
let initialHouseTimer: number | null = null;

export interface CameraControllerContext {
  scene: import('../mapSceneState').MapSceneState;
  applyHighlight: (mapObject: MapObject, marker: PIXI.Container) => void;
  clearPendingDeepLinkFocus: () => void;
  openModal: (modal: MapModalName, payload?: Partial<MapModalPayload>) => void;
}

export class CameraController {
  private ctx: CameraControllerContext;

  constructor(ctx: CameraControllerContext) {
    this.ctx = ctx;
  }

  // ── Singleton viewport animator (verbatim body, closure → this.ctx) ────────
  // All viewport.animate() calls must go through this function so they never
  // run concurrently (last-one-wins race conditions / jitter).
  // Pass `{ force: true }` to interrupt an in-flight anim (e.g. purchase fly-to
  // must win over the random initial-house camera).
  animate = (
    params: Parameters<Viewport['animate']>[0],
    opts?: { force?: boolean },
  ): void => {
    const { scene } = this.ctx;
    if (!scene.pixi.viewport) return;
    if (scene.viewport.animating && !opts?.force) return;
    if (scene.viewport.animating && opts?.force) {
      // pixi-viewport keeps animating until animate-end; drop the flag and
      // let the new animate() replace the plugin's target.
      scene.viewport.animating = false;
    }
    scene.viewport.animating = true;
    scene.pixi.viewport.once('animate-end', () => { scene.viewport.animating = false; });
    scene.pixi.viewport.animate(params);
  };

  // ── Initial camera: fly to a random owned house on first map load ──────────
  // Once per session. It used to fire only sometimes: the effect ran before the
  // map objects had loaded and never re-ran when they arrived; the "done" flag was
  // set before a 600 ms timer that any userData refetch then cancelled for good;
  // and a concurrent animation made animate() drop it silently. Now it waits for
  // the markers, the flag is set when the flight actually starts, re-renders do
  // not cancel it (only unmount does — cancelInitialHouseCamera), and it forces
  // its animation because every higher-priority camera move is gated out first.
  runInitialHouseCamera = (
    isPixiAppReady: boolean,
    markersReady: boolean,
    userId: UserData['user_id'] | undefined,
    shouldZoomToPlot: boolean,
    /** When set, My Houses / Top100 already requested a fly-to — skip the random house. */
    purchaseHighlightMapObjectId: number | null | undefined,
    /** A Telegram deep link focuses the camera itself — it wins. */
    hasDeepLinkFocus: boolean,
  ): void => {
    const { scene, applyHighlight } = this.ctx;
    if (didInitialHouseCamera || initialHouseTimer !== null) return;
    const mo = scene.data.mapObjects;
    if (!isPixiAppReady || !markersReady || !mo.length || !userId) return;
    if (shouldZoomToPlot) return; // onboarding has priority (and runs its own zoom)
    // A targeted fly-to (My Houses / Top100 / deep link) wins — consume the slot so
    // we don't yank the camera to a random owned house after it.
    if (purchaseHighlightMapObjectId || hasDeepLinkFocus) {
      didInitialHouseCamera = true;
      scene.analytics.didInitialHouseCamera = true;
      return;
    }

    const ownedHouses = mo.filter(
      o => o.type === 'house' && String(o.owner_id) === String(userId)
    );
    if (!ownedHouses.length) return;

    const house = ownedHouses[Math.floor(Math.random() * ownedHouses.length)];
    // Short delay so PIXI has settled after the markers were created.
    initialHouseTimer = window.setTimeout(() => {
      initialHouseTimer = null;
      didInitialHouseCamera = true;
      scene.analytics.didInitialHouseCamera = true;
      this.animate({ position: { x: house.x, y: house.y }, scale: 0.7, time: 1000 }, { force: true });
      const marker = scene.markers.realEstate.get(house.id);
      if (marker && !marker.destroyed) applyHighlight(house, marker);
    }, 600);
  };

  /** Map unmounted (e.g. a house entered at once): drop the pending flight for good. */
  cancelInitialHouseCamera = (): void => {
    if (initialHouseTimer === null) return;
    window.clearTimeout(initialHouseTimer);
    initialHouseTimer = null;
    didInitialHouseCamera = true;
  };

  // ── Deep-link camera focus: `startapp=plot_<id>` branch (verbatim body) ────
  zoomToPlot = (
    target: Extract<DeepLinkFocusTarget, { type: 'plot' }>,
    hasOnboardingCompleted: () => boolean,
  ): void => {
    const { scene, clearPendingDeepLinkFocus, openModal } = this.ctx;
    // Onboarding must be completed before following plot deep links
    if (!hasOnboardingCompleted()) return;
    // The announcement features a specific plot, but by the time the user taps
    // it may already be bought. Jump to the featured plot only if it's still a
    // free plot (owner_id === null); otherwise fall back to the nearest free plot.
    // If no free plots remain, show a "plot already taken" modal.
    const freePlots = scene.data.mapObjects.filter(
      (o) => o.type === 'plot' && o.owner_id === null && typeof o.x === 'number' && typeof o.y === 'number'
    );
    clearPendingDeepLinkFocus();
    if (freePlots.length === 0) {
      // No free plots anywhere — show modal instead of silent return
      openModal('plotTaken');
      return;
    }
    let dest = freePlots.find((o) => o.id === target.mapObjectId);
    if (!dest) {
      const center = scene.pixi.viewport?.center ?? { x: 0, y: 0 };
      dest = freePlots.reduce((best, o) => {
        const db = (best.x! - center.x) ** 2 + (best.y! - center.y) ** 2;
        const dO = (o.x! - center.x) ** 2 + (o.y! - center.y) ** 2;
        return dO < db ? o : best;
      }, freePlots[0]);
    }
    this.animate({ position: { x: dest.x!, y: dest.y! }, scale: 1.2, time: 900 });
  };

  // ── Deep-link camera focus: `startapp=cityhall` branch (verbatim body) ─────
  centerOnCityHall = (): void => {
    const { scene, clearPendingDeepLinkFocus } = this.ctx;
    const cityHallObj = scene.data.mapObjects.find((o) => o.type === 'cityhall');
    clearPendingDeepLinkFocus();
    if (cityHallObj && typeof cityHallObj.x === 'number' && typeof cityHallObj.y === 'number') {
      this.animate({ position: { x: cityHallObj.x, y: cityHallObj.y }, scale: 1.0, time: 900 });
    }
  };
}
